"use client";
/**
 * TrayIsland — the tray-flag mount of the Island (done rail phase 3). Lives as
 * its own component so react-query hooks run ONLY in the tray world: the
 * voice-v2-only Dock (and its provider-less tests) never touches
 * useQueryClient — the same mount discipline HomeShelf set (P1).
 *
 * Thumbs ride the SAME ['user-artifacts'] cache the home shelf fills, so in
 * practice this adds zero requests on a shelf-enabled home.
 */
import { useMemo, type RefObject } from "react";
import { Island } from "./Island";
import type { FleetRow } from "./fleet";
import { useUserArtifacts } from "@/components/studio-canvas/queries";
import { isCreation } from "@/components/creations/useTaskCreations";

export function TrayIsland({
  rows,
  open,
  onClose,
  anchorRef,
  quiet,
  hasUser,
}: {
  rows: FleetRow[];
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  quiet?: boolean;
  hasUser: boolean;
}) {
  const { artifacts } = useUserArtifacts({ enabled: hasUser });
  const thumbs = useMemo(() => {
    const m = new Map<string, { url: string }>();
    for (const a of artifacts) {
      if (!a.task_id || !isCreation(a)) continue;
      const url = a.thumb_url || a.url;
      if (!url) continue;
      const cur = m.get(a.task_id) as
        { url: string; created_at?: string } | undefined;
      if (!cur || (a.created_at ?? "") > (cur.created_at ?? "")) {
        m.set(a.task_id, { url, created_at: a.created_at } as { url: string });
      }
    }
    return m;
  }, [artifacts]);
  return (
    <Island
      rows={rows}
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      quiet={quiet}
      thumbs={thumbs}
    />
  );
}
